import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../api";
import { useAuth } from "../App";
import { useNavigate, useSearchParams } from "react-router-dom";

function Demande() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [dateDebut, setDateDebut] = useState(searchParams.get("date") || "");
  const [dateFin, setDateFin] = useState(searchParams.get("date") || "");
  const [demiJournee, setDemiJournee] = useState("entiere");
  const [commentaire, setCommentaire] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [balance, setBalance] = useState({ cp_restants: 0, cp_total: 30 });
  const [selecting, setSelecting] = useState("start");
  const [hovered, setHovered] = useState(null);
  const [existingLeaves, setExistingLeaves] = useState([]);
  const today = new Date();
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());

  const [feries, setFeries] = useState([]);

  useEffect(() => {
    const years = [today.getFullYear(), today.getFullYear() + 1, today.getFullYear() + 2];
    Promise.all(years.map(y =>
      axios.get(`${API}/feries/${y}`)
        .then(r => r.data.map(f => typeof f === 'string' ? f : f.date))
        .catch(() => [])
    )).then(results => setFeries(results.flat()));
  }, []);

  const isCollab = user.role === "collab" || user.role === "assistante";
  const isAssocie = user.role === "associe";
  const isDaf = user.role === "daf";
  const showSolde = true;

  useEffect(() => {
    const headers = { Authorization: "Bearer " + token };
    axios.get(API + "/leaves/mine", { headers })
      .then(leavesRes => {
        setExistingLeaves(leavesRes.data.filter(l => l.statut !== "refused"));
        axios.get(API + "/users/balance", { headers })
          .then(balanceRes => {
            const total = parseFloat(balanceRes.data.cp_total) || 30;
            // Seuls les CP sont décomptés du solde (RTT/Maladie/Maternité/Paternité/Pacs = orange = sans décompte)
            const poses = leavesRes.data
              .filter(l => l.statut === "approved" && l.type === "CP")
              .reduce((sum, l) => sum + parseFloat(l.jours || 0), 0);
            setBalance({ cp_restants: total - poses, cp_total: total });
          });
      });
  }, [token, user.role, isCollab]);

  const toStr = d => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return y + "-" + m + "-" + day;
  };

  const isFerie = (dateStr) => feries.includes(dateStr);

  const isAlreadyBooked = (dateStr) => {
    return existingLeaves.some(l => {
      const debut = l.date_debut.substring(0, 10);
      const fin = l.date_fin.substring(0, 10);
      return dateStr >= debut && dateStr <= fin;
    });
  };

  const countDays = (start, end) => {
    if (!start || !end) return 0;
    let days = 0;
    const [sy, sm, sd] = start.split("-").map(Number);
    const [ey, em, ed] = end.split("-").map(Number);
    let cur = new Date(sy, sm - 1, sd);
    const fin = new Date(ey, em - 1, ed);
    while (cur <= fin) {
      const str = toStr(cur);
      if (cur.getDay() !== 0 && cur.getDay() !== 6 && !isFerie(str)) days++;
      cur.setDate(cur.getDate() + 1);
    }
    return days;
  };

  const isJourUnique = dateDebut && dateFin && dateDebut === dateFin;
  const joursBase = countDays(dateDebut, dateFin);
  const jours = isJourUnique && demiJournee !== "entiere" ? 0.5 : joursBase;
  const solde = parseFloat(balance.cp_restants) || 0;
  const soldeApres = solde - jours;

  const handleDayClick = (dateStr) => {
    if (isFerie(dateStr) || isAlreadyBooked(dateStr)) return;
    if (selecting === "start" || (dateDebut && dateStr < dateDebut)) {
      setDateDebut(dateStr);
      setDateFin("");
      setDemiJournee("entiere");
      setSelecting("end");
    } else {
      setDateFin(dateStr);
      setDemiJournee("entiere");
      setSelecting("start");
    }
  };

  const isInRange = (dateStr) => {
    const end = hovered || dateFin;
    if (!dateDebut || !end) return false;
    return dateStr >= dateDebut && dateStr <= end;
  };

  const renderMonth = (month, year) => {
    const firstDay = new Date(year, month, 1).getDay();
    const offset = firstDay === 0 ? 6 : firstDay - 1;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const monthName = new Date(year, month).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
    const cells = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    const todayStr = toStr(today);
    return (
      <div key={year + "-" + month}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, textTransform: "capitalize", color: "#333", textAlign: "center" }}>{monthName}</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 30px)", gap: 1 }}>
          {["L","M","M","J","V","S","D"].map((d, i) => (
            <div key={i} style={{ textAlign: "center", fontSize: 10, color: "#aaa", padding: "3px 0", fontWeight: 500 }}>{d}</div>
          ))}
          {cells.map((d, i) => {
            if (!d) return <div key={"e" + i} />;
            const isWknd = d.getDay() === 0 || d.getDay() === 6;
            const dateStr = toStr(d);
            const ferie = isFerie(dateStr);
            const booked = isAlreadyBooked(dateStr);
            const isStart = dateStr === dateDebut;
            const isEnd = dateStr === (dateFin || hovered);
            const inRange = isInRange(dateStr);
            const isToday = dateStr === todayStr;
            const blocked = isWknd || ferie || booked;
            return (
              <div
                key={dateStr}
                onClick={() => { if (!blocked) handleDayClick(dateStr); }}
                onMouseEnter={() => { if (selecting === "end" && dateDebut && !blocked) setHovered(dateStr); }}
                onMouseLeave={() => setHovered(null)}
                style={{
                  textAlign: "center", fontSize: 12, padding: "5px 2px",
                  borderRadius: (isStart || isEnd) ? "50%" : inRange ? 3 : 4,
                  cursor: blocked ? "default" : "pointer",
                  background: (isStart || isEnd) ? "#7C6FE0" : booked ? "#e8e8e8" : inRange ? "#EAE8FC" : ferie ? "#FFF3CD" : "transparent",
                  color: (isStart || isEnd) ? "#fff" : booked ? "#bbb" : isWknd ? "#ccc" : ferie ? "#999" : isToday ? "#7C6FE0" : "#333",
                  fontWeight: (isToday || isStart || isEnd) ? 700 : 400,
                  outline: (isToday && !isStart && !isEnd) ? "2px solid #7C6FE0" : "none",
                  outlineOffset: -2,
                  textDecoration: ferie ? "underline" : booked ? "line-through" : "none",
                }}
              >
                {d.getDate()}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const months = [];
  for (let i = 0; i < 3; i++) {
    const m = (viewMonth + i) % 12;
    const y = viewYear + Math.floor((viewMonth + i) / 12);
    months.push(renderMonth(m, y));
  }

  const handleSubmit = async () => {
    if (submitting) return;
    if (!dateDebut || !dateFin) { setError("Veuillez selectionner les dates."); return; }
    if (jours <= 0) { setError("Dates invalides."); return; }
    if (isCollab && soldeApres < 0) {
      setError("Solde insuffisant.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(API + "/leaves",
        { type: "CP", date_debut: dateDebut, date_fin: dateFin, jours, commentaire, periode: demiJournee },
        { headers: { Authorization: "Bearer " + token } }
      );
      setSuccess((isAssocie || isDaf) ? "Conges enregistres !" : "Demande envoyee avec succes !");
      setError("");
      setTimeout(() => navigate("/"), 3000);
    } catch (err) {
      setError("Erreur lors de la soumission.");
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 1100 }}>
      <h2 style={{ marginBottom: 20, fontWeight: 500 }}>
        {(isAssocie || isDaf) ? "Saisir mes conges" : "Nouvelle demande"}
      </h2>
      <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: 12, padding: 24 }}>
        {error && <div style={{ background: "#FCEBEB", color: "#A32D2D", padding: "8px 12px", borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{error}</div>}
        {success && (
          <div style={{ background: "#EAF3DE", color: "#3B6D11", border: "1px solid #97C459", padding: "12px 16px", borderRadius: 8, marginBottom: 16, fontSize: 14, fontWeight: 600 }}>
            {success} Redirection dans 3 secondes...
          </div>
        )}

        {showSolde && !success && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#333", lineHeight: 1 }}>
              {solde} <span style={{ fontSize: 15, fontWeight: 400, color: "#888" }}>jours restants</span>
            </div>
            {jours > 0 && (
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 12, color: "#888" }}>Apres cette demande</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: soldeApres < 0 ? "#A32D2D" : "#3B6D11" }}>{soldeApres} jours</div>
              </div>
            )}
          </div>
        )}

        {!success && (
          <>
            {dateDebut && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: isJourUnique ? 12 : 20, padding: "10px 14px", background: "#f9f9f9", borderRadius: 8, fontSize: 13 }}>
                <span style={{ color: "#888" }}>Du</span>
                <span style={{ fontWeight: 500 }}>{new Date(dateDebut + "T12:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "long" })}</span>
                <span style={{ color: "#bbb" }}>→</span>
                <span style={{ fontWeight: 500 }}>{dateFin ? new Date(dateFin + "T12:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "long" }) : "..."}</span>
                {jours > 0 && (
                  <span style={{ marginLeft: "auto", background: "#EEEDFE", color: "#7C6FE0", padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                    {jours} jour{jours > 1 ? "s" : ""}
                  </span>
                )}
              </div>
            )}

            {isJourUnique && (
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                {["entiere", "matin", "apres-midi"].map(opt => (
                  <button key={opt} onClick={() => setDemiJournee(opt)}
                    style={{
                      padding: "6px 14px", borderRadius: 20, border: "1px solid", fontSize: 12, cursor: "pointer",
                      fontWeight: demiJournee === opt ? 600 : 400,
                      borderColor: demiJournee === opt ? "#7C6FE0" : "#ddd",
                      background: demiJournee === opt ? "#EEEDFE" : "#fff",
                      color: demiJournee === opt ? "#7C6FE0" : "#555"
                    }}>
                    {opt === "entiere" ? "Journee entiere" : opt === "matin" ? "Matin (0.5j)" : "Apres-midi (0.5j)"}
                  </button>
                ))}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
              <button onClick={() => { const d = new Date(viewYear, viewMonth - 1); setViewMonth(d.getMonth()); setViewYear(d.getFullYear()); }}
                style={{ background: "none", border: "1px solid #ddd", borderRadius: 6, padding: "4px 12px", cursor: "pointer" }}>{"<"}</button>
              <button onClick={() => { const d = new Date(viewYear, viewMonth + 1); setViewMonth(d.getMonth()); setViewYear(d.getFullYear()); }}
                style={{ background: "none", border: "1px solid #ddd", borderRadius: 6, padding: "4px 12px", cursor: "pointer" }}>{">"}</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 24, marginBottom: 20, justifyItems: "center" }}>
              {months}
            </div>

            <div style={{ display: "flex", gap: 16, marginBottom: 16, fontSize: 11, color: "#666" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 12, height: 12, background: "#FFF3CD", borderRadius: 2, border: "1px solid #ddd", display: "inline-block" }}></span>
                Jour ferie
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 12, height: 12, background: "#EAE8FC", borderRadius: 2, display: "inline-block" }}></span>
                Periode selectionnee
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 12, height: 12, background: "#e8e8e8", borderRadius: 2, display: "inline-block" }}></span>
                Deja pose
              </span>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 4 }}>Commentaire (optionnel)</label>
              <textarea value={commentaire} onChange={e => setCommentaire(e.target.value)}
                rows={2} style={{ width: "100%", padding: "7px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13, boxSizing: "border-box", resize: "none" }} />
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={handleSubmit} disabled={submitting || (isCollab && soldeApres < 0)}
                style={{ background: (submitting || (isCollab && soldeApres < 0)) ? "#ccc" : "#7C6FE0", color: "#fff", border: "none", borderRadius: 6, padding: "10px 28px", fontSize: 13, fontWeight: 600, cursor: (submitting || (isCollab && soldeApres < 0)) ? "default" : "pointer" }}>
                {submitting ? "Envoi en cours..." : (isAssocie || isDaf) ? "Enregistrer mes conges" : "Envoyer la demande"}
              </button>
              <button onClick={() => navigate("/")}
                style={{ background: "none", border: "1px solid #ddd", borderRadius: 6, padding: "10px 18px", fontSize: 13, cursor: "pointer" }}>
                Annuler
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Demande;